// @givmo/app-charity — code-based TanStack Router route tree (CPA-WEB1 scaffold, converted onto the
// org-console shell contract by CPA-WEB40).
//
// THREE AREAS:
//   - UNAUTHENTICATED: the auth screens as siblings off the root (NOT behind the authenticated
//     layout): /login-home (the charity's ROOT door — the guard's default target), /sign-in, /sign-up,
//     /sign-up/email, /verify-email, plus `/$module/sign-in` for a module mounted at a path in this
//     console (OM-12) — today only `/donations/sign-in`.
//   - AUTHENTICATED (behind the `_authenticated` pathless layout + `beforeLoad` guard): the console,
//     inside the chrome `PortalEntryGate` picks — the SaaS shell for a caller who has an organization
//     or a claim in review, the shell-less onboarding frame for one who has neither.
//
// ⚠ THE ROOT NEVER RENDERS (CPA-WEB40 · shell contract v3). `/` is one replace-redirect to wherever
// the signed-in person belongs — the package's ONE landing rule: their chosen default app, the last
// app they were in, the only openable app, the CLASS DEFAULT (Donations — every charity admin, a
// Global Admin included), the apps page. Root stays addressable so existing bookmarks keep working.
// The rule and why it cannot live in `beforeLoad` are in `./console-landing.tsx`.
//
// ⚠ THE DONATIONS MODULE IS MOUNTED AT `/donations` (§2 ruling 2): the certified statement is its
// landing (renamed Donations, A7), Payments its second page, the legacy per-grant drill-down its
// V2-OFF arm, and `/donations/team` its free Team page (T6.7, one parameterized route for every
// mounted module). The four old paths — `/dashboard`, `/grants`, `/grants/$grantId`, `/payment` —
// stay registered and REPLACE themselves, so a bookmark, a shared link, or the BLOCKING e2e
// attribution spec (until CPA-E2E13) still arrives somewhere true, and a pushed redirect cannot
// leave a back-button trap.
//
// ⚠ THE ADMIN CONSOLE (`/org` · `/org/team-access` · `/org/activity`) gates each page on its OWN
// CAPABILITY off the standing read (`RequireCapability`), never a tier; `/apps` is the statement
// page (a holder of `write_settings` is replaced onto `/org`); `/settings` is the gear's ONE composed
// settings page (the OM-17 cut-over; `/preferences`, its old address, replace-redirects to it).
//
// ⚠ THE NOT-FOUND SURFACE RENDERS INSIDE THE SHELL (T6.4): an authenticated catch-all, so an unknown
// address wears the Organization section and its control can compute the person's landing.
//
// The guard (parity with the admin/super-admin `BeamGuard`, which only checks `currentUser != null`)
// reads `context.auth.isAuthenticated`, once any sign-in still publishing has published (CPA-WEB86,
// `decideOnSettledSession`); a signed-out user hitting an authenticated route is
// redirected to the sign-in door, /login-home, with a `redirect` search param — except at the two
// EMAILED deep links, whose signed-out door is the CREATE-ACCOUNT one (/sign-up): the claim wizard
// `/claim/$charityId` (D-CP-64 — the grant-outreach email's button is that per-charity deep link, and
// its recipient has no account) and the invite landing `/invites/accept?token=…` (D-CP-65 — the
// admin-invite email's link, whose invitee is a colleague more likely new to Givmo than not). Each of
// those routes declares it (`staticData.signedOutDoor`); the ONE guard reads it.
//
// Routing is CODE-BASED (no @tanstack/router-plugin codegen) — a deliberate scaffold choice that
// keeps the route tree explicit and trivially unit-testable with an in-memory router.

import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
  type StaticDataRouteOption,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { Toaster } from '@givmo/design-system';
import {
  COMPLIANCE_RETIRED_ROUTES,
  COMPLIANCE_ROUTES,
  COMPLIANCE_SLUG,
  FILING_FORM_PATH,
  FILING_PANEL_PATH,
  FILING_RULE_PATH,
  FILING_SEND_PATH,
  OBLIGATION_DETAIL_PATH,
  SIGNING_PATH,
} from '@givmo/compliance';
import { AppError } from '../components/app-error';
import { decideOnSettledSession, requiresEmailVerification } from '../auth/auth-context';
import {
  CAPABILITY_READ_EVENTS,
  CAPABILITY_WRITE_MEMBER_ACCESS,
  CAPABILITY_WRITE_SETTINGS,
  parseSettingsOriginSearch,
  settingsOriginSearchFor,
} from '@givmo/org-console';
import { CharityConsoleProvider } from '../console/charity-console-context';
import {
  ACTIVITY_ROUTE,
  APPS_ROUTE,
  DONATIONS_GRANT_DETAIL_PATH,
  DONATIONS_PAYMENTS_ROUTE,
  DONATIONS_ROUTE,
  ORG_ROUTE,
  ORGANIZATION_DEEP_LINK_PATH,
  RETIRED_DASHBOARD_ROUTE,
  RETIRED_FILING_REGISTER_DEADLINES_ROUTE,
  RETIRED_FILING_REGISTER_DOCUMENTS_ROUTE,
  RETIRED_FILING_REGISTER_FACTS_ROUTE,
  RETIRED_FILING_REGISTER_OBLIGATION_PATH,
  RETIRED_FILING_REGISTER_REVIEW_ROUTE,
  RETIRED_FILING_REGISTER_ROUTE,
  RETIRED_GRANT_DETAIL_PATH,
  RETIRED_GRANTS_ROUTE,
  RETIRED_PAYMENT_ROUTE,
  RETIRED_PREFERENCES_ROUTE,
  RETIRED_TEAM_ACCESS_ROUTE,
  SETTINGS_ROUTE,
  TEAM_ACCESS_ROUTE,
} from '../console/console-routes';
import { ActiveOrgProvider } from '../org/active-org-context';
import { PortalEntryGate } from '../org/claim-first-gate';
import { OrgScopedBoundary } from '../org/org-scoped-boundary';
import { LoginHomeDoor, SignUpDoor } from '../features/auth/auth-door';
import { AUTH_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from '../features/auth/auth-routes';
import { ModuleSignInPage, moduleDoorFor } from '../features/auth/module-sign-in-page';
import { SignInScreen } from '../features/auth/sign-in-screen';
import { LostCompanyAccessScreen } from '../features/auth/lost-company-access-screen';
import {
  OrganizationSsoCallbackScreen,
  OrganizationSsoScreen,
} from '../features/auth/organization-sso-screen';
import { ORGANIZATION_SSO_CALLBACK_PATH } from '@givmo/passkeys';
import { SignUpEmailScreen } from '../features/auth/sign-up-email-screen';
import { VerifyEmailScreen } from '../features/auth/verify-email-screen';
import { AppsPage } from '../features/apps/apps-page';
import { NotFound } from '../components/not-found';
import { OrgSearchPage } from '../features/claim/org-search-page';
import { ClaimWizardEntry } from '../features/claim/claim-wizard-entry';
import { ClaimsListPage } from '../features/claim/claims-list-page';
import { ClaimDetailPage } from '../features/claim/claim-detail-page';
import { AcceptInvitePage } from '../features/admins/accept-invite-page';
import { PaymentPage } from '../features/payment/payment-page';
import { ProfilePage } from '../features/profile/profile-page';
import {
  CharityProfileQrRoute,
  CharityProfileRoute,
} from '../features/profile/charity-profile-page';
import { parseProfileSearch, PROFILE_ROUTES } from '../features/profile/profile-routes';
import { SettingsPage } from '../features/settings/settings-page';
import { SETTINGS_ROUTES } from '../features/settings/settings-routes';
import { SupportPage } from '../features/support/support-page';
import { GrantDetailScreen } from '../features/grants/grants-screen';
import { DonationsLanding } from '../features/donations/donations-landing';
import { validateStatementSearch } from '../features/grants/statement-search';
import {
  FundraisersPage,
  FundraiserCreatePage,
  FundraiserEditorPage,
  EmailPage,
  parseEmailSearch,
  FUNDRAISERS_SLUG,
  FUNDRAISERS_ROUTES,
  FUNDRAISER_EDITOR_PATH,
  FUNDRAISER_CAMPAIGN_PATH,
  FUNDRAISER_LAYERS_PATH,
  FUNDRAISER_DONORS_PATH,
  FUNDRAISER_MESSAGES_PATH,
  FUNDRAISER_EMAIL_DESIGN_PATH,
  EmailDesignerPage,
  FUNDRAISER_EMAILS_PATH,
  FUNDRAISER_EMAIL_PATH,
  FUNDRAISER_QR_PATH,
  FUNDRAISER_BOARD_ASK_PATH,
  BoardAskPage,
  FUNDRAISER_SEASON_PATH,
  FundraiserQrDesignPage,
  SeasonSetupPage,
  fundraisersViewFromSearch,
  parseFundraisersListSearch,
} from '@givmo/fundraisers-module';
import { CharityFundraisersMount } from '../features/fundraisers-module/fundraisers-mount';
import { useCharityLogoSrc } from '../features/org-qr/use-charity-logo';
import {
  CharityCrmContactsMount,
  CharityCrmDonorMount,
  CharityCrmImportMount,
  CharityCrmNewSegmentMount,
  CharityCrmSegmentMount,
  CharityCrmSegmentsMount,
} from '../features/crm-module/crm-mount';
import { CRM_MODULE_ROUTES } from '../features/crm-module/crm-routes';
import { AnalyticsPage } from '../features/analytics/analytics-page';
import { ANALYTICS_ROUTES } from '../features/analytics/analytics-routes';
import {
  ComplianceCalendarPage,
  ComplianceDocumentsPage,
  ComplianceFilingFormPage,
  ComplianceFilingRulePage,
  ComplianceFilingSendPage,
  ComplianceFilingsPage,
  ComplianceReviewPage,
  ComplianceSigningPage,
} from '../features/compliance/compliance-mount';
import { CharityStepUpHostProvider } from '../features/step-up/charity-step-up-host';
import type { ReactNode } from 'react';
import { DonationsModuleGate } from '../console/donations-module-gate';
import { BOOKS_ROUTES } from '@givmo/books';
import { CharityBooksMount } from '../features/books/books-mount';
import { ActivityPage } from '../features/org/activity-page';
import { ModuleTeamRoute } from '../features/org/module-team-page';
import { OrgPage } from '../features/org/org-page';
import { RequireCapability } from '../features/org/require-capability';
import { TeamAccessPage } from '../features/org/team-access-page';
import { ConsoleSettingsPage } from '../features/preferences/preferences-page';
import { EnrollmentPage } from '../features/enrollment/enrollment-page';
import { EnrollmentRecordPage } from '../features/enrollment/enrollment-record-page';
import { EnrollmentBackfillPage } from '../features/enrollment/enrollment-backfill-page';
import {
  ENROLLMENT_BACKFILL_PATH,
  ENROLLMENT_RECORD_PATH,
  ENROLLMENT_ROUTES,
} from '../features/enrollment/enrollment-routes';
import {
  isFundraisersModuleEnabled,
  isCrmImportEnabled,
  isCrmModuleEnabled,
  isBooksModuleEnabled,
  isCharityDonorAnalyticsEnabled,
  isComplianceUiEnabled,
  isEnrollmentUiEnabled,
  isOrgQrDesignEnabled,
  isEmailDesignerEnabled,
  isProfilePageEnabled,
} from '../lib/env';
import { UnsavedChangesProvider } from '../lib/unsaved-changes';
import { ConsoleLanding } from './console-landing';
import { ConsoleLayoutProbes } from './console-layout-probes';
import type { CharityRouterContext } from './context';
import { OrganizationDeepLinkPage } from './organization-deep-link';
import { sanitizeRedirectTarget } from './redirect-target';
import { SIGN_IN_LANDING_STATE, sidebarFrameOf } from '../console/sidebar-frame';
import {
  MeetingsPage as SharedBoardMeetingsPage,
  MeetingDetailPage as SharedBoardMeetingDetailPage,
  PacketPage as SharedBoardPacketPage,
  InMeetingRecordPage as SharedBoardInMeetingRecordPage,
  MinutesPage as SharedBoardMinutesPage,
  RosterPage as SharedBoardRosterPage,
  MemberDetailPage as SharedBoardMemberDetailPage,
  CommitteesPage as SharedBoardCommitteesPage,
  CommitteePage as SharedBoardCommitteePage,
  EvaluationsPage as SharedBoardEvaluationsPage,
  Form990Page as SharedBoardForm990Page,
  ScheduleOPage as SharedBoardScheduleOPage,
  ReturnReceiptsPage as SharedBoardReturnReceiptsPage,
  BoardAskPage as SharedBoardAskPage,
  BoardAsksPage as SharedBoardAsksPage,
  HoldReportPage as SharedBoardHoldReportPage,
  ConsentsPage as SharedBoardConsentsPage,
  ConsentDetailPage as SharedBoardConsentDetailPage,
  ConflictsRegisterPage as SharedBoardConflictsRegisterPage,
  CoiResponsePage as SharedBoardCoiResponsePage,
  RecordsPage as SharedBoardRecordsPage,
  RecordDetailPage as SharedBoardRecordDetailPage,
  GovernancePage as SharedBoardGovernancePage,
  GOVERNANCE_TABS as BOARD_GOVERNANCE_TABS,
  FORM_990_TABS as BOARD_FORM_990_TABS,
  RETIRED_COMPENSATION_TAB as BOARD_RETIRED_COMPENSATION_TAB,
  BOARD_IMPORT_ROUTES,
  BOARD_ROUTE,
  BOARD_SLUG,
  ImportBatchesPage as SharedBoardImportBatchesPage,
  ImportBatchPage as SharedBoardImportBatchPage,
  ProposedBatchPage as SharedBoardProposedBatchPage,
  ImportUploadsPage as SharedBoardImportUploadsPage,
  DirectorBoardsPage as SharedBoardDirectorBoardsPage,
  DirectorInvitationPage as SharedBoardDirectorInvitationPage,
  DirectorMovePage as SharedBoardDirectorMovePage,
  DirectorDoorPage as SharedBoardDirectorDoorPage,
  directorRedeemSearch,
  DirectorMeetingsPage as SharedBoardDirectorMeetingsPage,
  DirectorMeetingDetailPage as SharedBoardDirectorMeetingDetailPage,
  DirectorConsentsPage as SharedBoardDirectorConsentsPage,
  DirectorConsentDetailPage as SharedBoardDirectorConsentDetailPage,
  DirectorDisclosuresPage as SharedBoardDirectorDisclosuresPage,
  DirectorAnnualDisclosurePage as SharedBoardDirectorAnnualDisclosurePage,
  DirectorPacketPage as SharedBoardDirectorPacketPage,
  DirectorSignInSecurityPage as SharedBoardDirectorSignInSecurityPage,
  DirectorRecordsPage as SharedBoardDirectorRecordsPage,
  DirectorRecordDetailPage as SharedBoardDirectorRecordDetailPage,
  // CBG-YR4-START, -ABOUT: the member's own getting-started list, and About the board.
  DirectorGettingStartedPage as SharedBoardDirectorGettingStartedPage,
  DirectorAboutPage as SharedBoardDirectorAboutPage,
  DirectorMembersPage as SharedBoardDirectorMembersPage,
  DirectorCommitteesPage as SharedBoardDirectorCommitteesPage,
  // CBG-YR4-ASSESS-WEB: the person answering their board's self-assessment.
  DirectorSelfAssessmentPage as SharedBoardDirectorSelfAssessmentPage,
  DirectorReturnPage as SharedBoardDirectorReturnPage,
  DirectorAskPage as SharedBoardDirectorAskPage,
  CalendarConnectReturnPage as SharedBoardCalendarConnectReturnPage,
  CALENDAR_CONNECT_RETURN_PAGE as BOARD_CALENDAR_CONNECT_RETURN_PAGE,
} from '@givmo/board';
import { CharityBoardMount } from '../features/board/board-mount';
import { CharityDirectorHost } from '../features/board/director-host';
import {
  confirmDirectorEmailFirst,
  redirectSignedInDirector,
  requireDirectorAccount,
  requireDirectorConfirmation,
} from '../features/board/director-guard';
import { DIRECTOR_ROUTES } from '../features/board/director-routes';
import { isBoardUiEnabled } from '../lib/env';

/** Parse the optional `redirect` (post-auth bounce target) off a route's search params. */
function parseRedirectSearch(search: Record<string, unknown>): { redirect?: string } {
  const redirectTarget = sanitizeRedirectTarget(search.redirect);
  return redirectTarget ? { redirect: redirectTarget } : {};
}

/**
 * The director's two email forms (`redirect`, plus Board's `arrival`): `another_account` means one of
 * Board's two "use another Givmo account" steps opened them, so the sign-in form's lock notice offers
 * no organization door (cut 1007e's fold, Board's B-F4). Both routes declare it, and the forms carry
 * it when they cross to each other (Board's R1; `features/auth/director-arrival.tsx`,
 * `directorEmailSearch`).
 */
function parseDirectorEmailSearch(search: Record<string, unknown>): {
  redirect?: string;
  arrival?: 'another_account';
} {
  return {
    ...parseRedirectSearch(search),
    ...(search.arrival === 'another_account' ? { arrival: 'another_account' as const } : {}),
  };
}

function parseVerifyEmailSearch(search: Record<string, unknown>): {
  redirect?: string;
  otpSent?: boolean;
  otpSendFailed?: boolean;
} {
  return {
    ...parseRedirectSearch(search),
    ...(search.otpSent === true || search.otpSent === 'true' ? { otpSent: true } : {}),
    ...(search.otpSendFailed === true || search.otpSendFailed === 'true'
      ? { otpSendFailed: true }
      : {}),
  };
}

/**
 * Parse the optional `back` (the door a signed-out sub-flow returns to) off a route's search params:
 * the same sanitizer as `redirect`, so only a same-origin path survives.
 */
function parseBackSearch(search: Record<string, unknown>): { back?: string } {
  const back = sanitizeRedirectTarget(search.back);
  return back ? { back } : {};
}

/** Parse the emailed invite `token` off the accept-invite route's search params. */
function parseInviteTokenSearch(search: Record<string, unknown>): { token?: string } {
  const token = typeof search.token === 'string' ? search.token : undefined;
  return token ? { token } : {};
}

/** Devtools are dev-only and code-split so they never ship in the production bundle. */
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    );

// ── Root ───────────────────────────────────────────────────────────────────────────────────────
export const rootRoute = createRootRouteWithContext<CharityRouterContext>()({
  errorComponent: AppError,
  component: function RootComponent() {
    return (
      <>
        <Outlet />
        {/* App-wide toast host (later mutation surfaces toast here). */}
        <Toaster />
        <Suspense fallback={null}>
          <RouterDevtools />
        </Suspense>
      </>
    );
  },
});

// ── Unauthenticated area — auth screens as siblings off the root. ───────────────────────────────

// /login-home — the unified auth door (identical card to /sign-up; its email default is the
// SIGN-IN form, since guard bounces and sign-outs skew returning). KEEPS the `page-login-home`
// testid (the guard tests assert it). `redirect` carries the guard's intended post-login target.
const loginHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.loginHome,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectAuthenticatedFromPublicAuth,
  component: LoginHomeDoor,
});

// /sign-in — email/password form. `redirect` carries the post-login bounce target.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.signIn,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectAuthenticatedFromPublicAuth,
  component: SignInScreen,
});

// /sign-up — the same unified auth door; its email default is the CREATE form, since arrivals here
// skew new: the claim wizard's guard forwards a signed-out caller of the outreach email's deep link
// (`/claim/<id>`, D-CP-64) here, `redirect` in hand. The email itself never links to /sign-up.
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.signUp,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectAuthenticatedFromPublicAuth,
  component: SignUpDoor,
});

// /sign-up/email — name + email + password. Creates the Firebase user, then routes to /verify-email.
// A sibling route rather than a step inside the chooser, so browser Back returns to the options.
const signUpEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.signUpEmail,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectAuthenticatedFromPublicAuth,
  component: SignUpEmailScreen,
});

// /verify-email — backend-OTP email verification (shown after sign-up; the user is already signed in).
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.verifyEmail,
  validateSearch: parseVerifyEmailSearch,
  beforeLoad: requireEmailVerificationBeforeLoad,
  component: VerifyEmailScreen,
});

// /lost-company-access — the way back in for someone whose company locks how they sign in
// (AS-WEB26): every door's "Lost access to your company sign-in?" opens it. Signed out by nature, so a
// signed-in arrival goes where any public auth screen sends it. `back` is the door to return to.
const lostCompanyAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.lostCompanyAccess,
  validateSearch: parseBackSearch,
  beforeLoad: ({ context }) => redirectAuthenticatedFromPublicAuth({ context, search: {} }),
  component: LostCompanyAccessScreen,
});

// /sso — the organization door's step (AS-WEB30): every door's "Continue with Organization SSO"
// opens it. Signed out by nature, so a signed-in arrival goes where any public auth screen sends it.
// `redirect` is where signing in lands; `back` the door to return to.
const organizationSsoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.organizationSso,
  validateSearch: (search: Record<string, unknown>) => ({
    ...parseRedirectSearch(search),
    ...parseBackSearch(search),
  }),
  beforeLoad: redirectAuthenticatedFromPublicAuth,
  component: () => <OrganizationSsoScreen />,
});

// /sso/callback — where the employer's sign-in page sends the browser back, with a one-use handoff
// code or an error (AS-WEB30).
// ⚠ THIS PATH IS AN OPERATOR CONTRACT. Joined to each environment's charity origin it is what is
// registered in the backend's `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`, matched EXACTLY.
// NO signed-in guard, deliberately: the sign-in this page finishes changes the identity, which
// re-runs the current route's guards (`app.tsx`), and a guard here would land the person at the
// default before the page lands them where they were going. A bare visit goes on to `/sso`, whose
// own guard lands a signed-in visitor.
const organizationSsoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORGANIZATION_SSO_CALLBACK_PATH,
  validateSearch: (search: Record<string, unknown>): { handoff_code?: string; error?: string } => ({
    ...(typeof search.handoff_code === 'string' ? { handoff_code: search.handoff_code } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: OrganizationSsoCallbackScreen,
});

// ── The per-module door (OM-12 mechanic 4) ─────────────────────────────────────────────────────
//
// The generic door serves mounted modules; a reserved static alias protects it when a module has
// a flat resource-id route. Both call the same handler and page. The parameter is the module's
// PATH SEGMENT, validated against the modules this build actually serves — an unknown segment lands
// on the charity's own ROOT door, `/login-home` (§9-8), rather than on a branded door for something
// that is not here.
function beforeModuleSignIn({
  context,
  params,
  search,
}: {
  context: CharityRouterContext;
  params: { module: string };
  search: { redirect?: string };
}) {
  const door = moduleDoorFor(params.module);
  if (door === undefined) {
    throw redirect({ to: AUTH_ROUTES.loginHome, search, replace: true });
  }
  // Already signed in: skip the door entirely and go where it would have taken them. The DEFAULT
  // is the module itself — the arrive-through-module rule — not the landing computation, which
  // would send an Owner to the organization's page instead of the thing they opened.
  return decideOnSettledSession(context.auth, (session) => {
    if (session.isAuthenticated) {
      throw redirect({
        to: sanitizeRedirectTarget(search.redirect) ?? door.path,
        replace: true,
        state: SIGN_IN_LANDING_STATE,
      });
    }
  });
}

const moduleSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$module/sign-in',
  validateSearch: parseRedirectSearch,
  beforeLoad: beforeModuleSignIn,
  component: function ModuleSignInRoute() {
    const { module } = moduleSignInRoute.useParams();
    return <ModuleSignInPage segment={module} />;
  },
});

// Reserved path alias: the flat fundraiser-id route otherwise wins over $module/sign-in.
const fundraisersSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: FUNDRAISERS_ROUTES.signIn,
  validateSearch: parseRedirectSearch,
  beforeLoad: ({ context, search }) =>
    beforeModuleSignIn({ context, search, params: { module: FUNDRAISERS_SLUG } }),
  component: () => <ModuleSignInPage segment={FUNDRAISERS_SLUG} />,
});

// Reserved path alias: the flat obligation-id route otherwise wins over $module/sign-in.
const complianceSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: COMPLIANCE_ROUTES.signIn,
  validateSearch: parseRedirectSearch,
  beforeLoad: ({ context, search }) =>
    beforeModuleSignIn({ context, search, params: { module: COMPLIANCE_SLUG } }),
  component: () => <ModuleSignInPage segment={COMPLIANCE_SLUG} />,
});

// ── Authenticated route guard (shared) ──────────────────────────────────────────────────────────
// A signed-out user is sent to a door; an unverified user whose sign-in methods are all
// OTP-repairable (password, microsoft.com — GCIP mints Microsoft users unverified) is sent through
// the OTP gate. Attesting providers (google.com) arrive verified and pass straight through.
//
// WHICH door is the matched leaf's to declare (`staticData.signedOutDoor`, below), and the default
// is the sign-in door — a guard bounce skews returning. The routes that declare otherwise are the
// two EMAILED deep links, whose cold arrival has no account:
//   - the claim wizard (D-CP-64): the grant-outreach email's button is
//     `https://charity.givmo.io/claim/<id>`, a hard-coded per-charity deep link (backend
//     `grant_onboarding_candidate_service.py`, pinned by the brain resolver's `^/claim/[^/]+$`), so
//     a cold recipient with no account arrives THERE — and a sign-in form is a door she cannot use;
//   - the invite landing (D-CP-65): the admin-invite email's link is
//     `${CHARITY_PORTAL_BASE_URL}/invites/accept?token=…` (backend `org_invite_service.py`), and its
//     invitee — a colleague at the same nonprofit — is more likely new to Givmo than not; the wrong
//     default is lopsided (a newcomer on the sign-in form is stuck until they find the small link,
//     an account holder on the create form taps "Sign in").
// Both are sent to the create-account door instead, the sanitized `redirect` in hand — the invite's
// query string included: the sanitizer keeps a query, and the doors' `redirect`/`navigate` carry a
// `to` with one intact (`invite-accept-deep-link.test.tsx` pins the token's whole road) — and after
// account creation + email verification the doors' existing redirect-honouring lands them back on
// that charity's claim, or on the invite with its token. The guard reads the declaration off the
// pending `matches` (root → leaf, the innermost declaration winning) rather than off the pathname:
// the layout's `beforeLoad` runs BEFORE the leaf's, so a `beforeLoad` on the wizard route itself
// would never run for a signed-out caller, and matching a path shape here would be a second
// spelling of the route.

/** The doors the guard can send a signed-out caller to. */
type SignedOutDoor = typeof AUTH_ROUTES.loginHome | typeof AUTH_ROUTES.signUp;

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * The door the authenticated layout's guard sends a SIGNED-OUT caller of this route to, the
     * sanitized `redirect` in hand. Absent ⇒ the sign-in door. Declared by the two emailed deep
     * links: the claim wizard (D-CP-64) and the invite landing (D-CP-65).
     */
    signedOutDoor?: SignedOutDoor;
    /**
     * Declared by a route that shows the CHARITY Terms in flow, so the onboarding chrome's footer
     * must not offer the PLATFORM Terms beside it (D-CP-70). Absent ⇒ the footer's Terms link
     * renders, which is every other pre-claim screen. The ONE reader is the footer itself
     * (`components/onboarding-shell.tsx`); declared by the claim wizard alone.
     */
    hidesOnboardingFooterTerms?: true;
  }
}

/**
 * The pending matches, root → leaf, off the guard's context — read by PRESENCE rather than named
 * in the guard's parameter type. ⚠ The context's `matches` is typed as a union over the REGISTERED
 * router's route tree, so a guard whose parameter type names it (even as `unknown[]`) is compared
 * against that union while the tree — which contains the layout this guard sits on — is still
 * being inferred; TypeScript resolves the cycle by making the whole tree `any`, and every typed
 * `navigate` in the app fails with it. The runtime shape is router-core's `RouteMatch`; only
 * `staticData` is read.
 */
function pendingMatches(ctx: object): ReadonlyArray<{ staticData?: StaticDataRouteOption }> {
  const matches = 'matches' in ctx ? ctx.matches : undefined;
  return Array.isArray(matches)
    ? (matches as ReadonlyArray<{ staticData?: StaticDataRouteOption }>)
    : [];
}

/**
 * The matched leaf's declared signed-out door, else the sign-in door. Read leaf → root, so the
 * innermost declaration wins: a layout that declares a door for its whole family still yields to a
 * leaf that declares its own. Exported for its pins (`signed-out-door.test.tsx`).
 */
export function signedOutDoorFor(ctx: object): SignedOutDoor {
  for (const match of [...pendingMatches(ctx)].reverse()) {
    const door = match.staticData?.signedOutDoor;
    if (door !== undefined) return door;
  }
  return AUTH_ROUTES.loginHome;
}

/**
 * Exported for the door-precedence pins, which mount it on a small tree of their own.
 *
 * Decides on the SETTLED session (CPA-WEB86), as every guard here does: a door navigates the moment
 * its sign-in resolves, before the auth context publishes the user behind the `users` row bootstrap
 * (AS-SUITE7), and reading that signed-out snapshot sent the person who had just signed in to
 * `/login-home` until the bootstrap answered. The guard waits for the publish instead.
 */
export const requireAuthBeforeLoad = (ctx: {
  context: CharityRouterContext;
  location: { href: string };
}) => {
  const { context, location } = ctx;
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) {
      throw redirect({ to: signedOutDoorFor(ctx), search: { redirect: location.href } });
    }
    if (requiresEmailVerification(session.user)) {
      throw redirect({ to: AUTH_ROUTES.verifyEmail, search: { redirect: location.href } });
    }
  });
};

function redirectAuthenticatedFromPublicAuth({
  context,
  search,
}: {
  context: CharityRouterContext;
  search: { redirect?: string };
}) {
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) return;
    if (requiresEmailVerification(session.user)) {
      throw redirect({
        to: AUTH_ROUTES.verifyEmail,
        search: sanitizeRedirectTarget(search.redirect)
          ? { redirect: sanitizeRedirectTarget(search.redirect) }
          : {},
      });
    }
    throw redirect({
      to: sanitizeRedirectTarget(search.redirect) ?? DEFAULT_AUTHENTICATED_ROUTE,
      state: SIGN_IN_LANDING_STATE,
    });
  });
}

function requireEmailVerificationBeforeLoad({
  context,
  location,
  search,
}: {
  context: CharityRouterContext;
  location: { href: string };
  search: { redirect?: string };
}) {
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) {
      throw redirect({ to: AUTH_ROUTES.loginHome, search: { redirect: location.href } });
    }
    if (!requiresEmailVerification(session.user)) {
      throw redirect({
        to: sanitizeRedirectTarget(search.redirect) ?? DEFAULT_AUTHENTICATED_ROUTE,
        state: SIGN_IN_LANDING_STATE,
      });
    }
  });
}

// ── Authenticated layout (pathless) + route guard ───────────────────────────────────────────────
// Hosts the console inside the chrome the portal-entry gate picks, behind the auth guard.
const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: requireAuthBeforeLoad,
  component: function AuthenticatedLayout() {
    // The active-org context wraps the whole authenticated area: it owns the single memberships read
    // that both the shell's org switcher and every feature page consume. Mounted here (behind the
    // guard) so the read only fires for signed-in users.
    //
    // <CharityConsoleProvider> sits directly under it: the console's ONE standing read
    // (`GET /org/{ref}/me`), mounted the moment an organization resolves, and shared by the landing
    // rule, the shell's cluster, the app grid and the org pages. Above the gate, because the gate
    // renders the shell — which reads it.
    //
    // <PortalEntryGate> sits ABOVE the chrome rather than inside it (CPA-WEB10): the entry state does
    // not merely choose what to render in the content area, it chooses WHICH CHROME wraps it — the
    // SaaS shell for a caller who has an organization or a claim in review, the shell-less onboarding
    // frame for one who has neither. The gate renders <AppShell> / <OnboardingShell> itself.
    //
    // <MembershipsFreshness> is a render-free leaf directly under the provider: the provider mounts
    // ONCE for the whole authenticated area, so `refetchOnMount` never fires again and in-console
    // navigation re-asked the memberships read nothing. It supplies that trigger (CPA-WEB46).
    //
    // <OrganizationDeepLinkProbe> is its sibling (CPA-BE29): the organization deep link's decision
    // changes the active organization, which re-keys <OrgScopedBoundary> below — so it is made
    // here, above the boundary, where it survives the remount it causes. It acts only while the
    // deep-link route is the one matched, and the route's own component paints nothing. It is
    // handed the link's raw `to` (CCM-R1-SIGN-LINK-ORG) and vets it itself: TanStack keeps an
    // unknown search param as typed, so nothing upstream has.
    //
    // <ClaimWizardRefreshProbe> is the third of the family (CPA-WEB55), here for the same reason and
    // one more: the claim wizard's entry must decide its held-membership short-circuit on a
    // REFRESHED list, and it is remounted BOTH by the boundary below and by <PortalEntryGate>'s
    // chrome swap. An observer inside the entry was measured asking twice; this one asks once and
    // publishes the answer to the entry through the query cache. Unlike the two above it, the
    // wizard's route DOES paint — only the asking moved up here.
    //
    // The three are ONE component, `<ConsoleLayoutProbes>`, because the preview harness's own layout
    // mounts it too: when the third reached this layout alone, the harness's claim wizard never left
    // its loading card (CPA-WEB85).
    return (
      <ActiveOrgProvider>
        <ConsoleLayoutProbes />
        <CharityConsoleProvider>
          {/* The unsaved-changes guard wraps the step-up host, whose "Sign in again" tells it the
              leave is deliberate once the sign-out has happened (AS-PWL-2e). */}
          <UnsavedChangesProvider>
            {/* The console's one "Confirm it's you" host, for every gate that names none (CCM-R1-SIGN-WEB). */}
            <CharityStepUpHostProvider>
              <PortalEntryGate>
                <OrgScopedBoundary>
                  <Outlet />
                </OrgScopedBoundary>
              </PortalEntryGate>
            </CharityStepUpHostProvider>
          </UnsavedChangesProvider>
        </CharityConsoleProvider>
      </ActiveOrgProvider>
    );
  },
});

// ── The console's own pages ────────────────────────────────────────────────────────────────────

// The index route computes where this person belongs and replaces itself with it. It renders no
// content at all — see `./console-landing.tsx` for the rule and its branch order.
const indexRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/',
  component: ConsoleLanding,
});

// `/apps` — two answers, no third page (D3/D6): a holder of `write_settings` is replaced onto the
// Admin Console's Apps page; everyone else gets the "your apps are in the menu" statement.
const appsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: APPS_ROUTE,
  component: AppsPage,
});

// The dissolved dashboard. Replaces itself with the root, which re-runs the landing rule.
const retiredDashboardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_DASHBOARD_ROUTE,
  beforeLoad: () => {
    throw redirect({ to: DEFAULT_AUTHENTICATED_ROUTE, replace: true });
  },
});

// Claim flow (CPA-WEB2):
//   /claim                     — org search (claim entry point)
//   /claim/$charityId          — claim wizard for a selected org (attest → submit → branch), behind
//                                its ENTRY's two doors (CPA-WEB52 + CPA-WEB53): a caller who already
//                                administers the charity is entered into their console, and a
//                                signed-in personal-email address is refused; a cached terminal
//                                result (CPA-WEB51) outranks both. `features/claim/claim-wizard-entry.tsx`.
//                                The grant-outreach email's deep link (D-CP-64): SIGNED OUT, the
//                                guard sends the caller to the create-account door, not sign-in.
//   /claims                    — the caller's claim requests list
//   /claims/$claimRequestId    — a single claim's status + Tier-2 document upload
const claimSearchRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/claim',
  component: OrgSearchPage,
});

const claimWizardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/claim/$charityId',
  // D-CP-64 — the outreach email's destination, whose recipient has no account yet: a signed-out
  // caller is forwarded to the CREATE-ACCOUNT door (see `signedOutDoorFor`). Signed in, the entry's
  // doors decide exactly as before.
  //
  // D-CP-70 — and the onboarding chrome's footer drops its Terms link here, at this address alone:
  // the ceremony under it reads the CHARITY Terms in flow, so a footer word pointing at the
  // PLATFORM Terms is the same word for a different document on the same screen. Declared on the
  // ROUTE rather than set by the page because every state this address can be in is the wizard —
  // the entry's loading card, the personal-email refusal, the ceremony, and the terminal panel that
  // confirms the acceptance — and a link that came and went between them would be its own defect.
  staticData: { signedOutDoor: AUTH_ROUTES.signUp, hidesOnboardingFooterTerms: true },
  component: ClaimWizardEntry,
});

const claimsListRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/claims',
  component: ClaimsListPage,
});

const claimDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/claims/$claimRequestId',
  component: ClaimDetailPage,
});

// The invite door (CPA-WEB2). The legacy `/admins` page retired with CPA-WEB41: the address is an
// unknown path (the shell's not-found), never a redirect; admin management is the Admin Console's
// Team access page (`/org/team-access`).
//   /invites/accept    — the invitee's accept-invite landing (`?token=…`). Behind the auth guard AND
//                        inside the portal-entry gate (claim-first): the remount-safe redeem query
//                        relies on the gate's chrome swap. The admin-invite email's link (D-CP-65):
//                        SIGNED OUT, the guard sends the invitee to the create-account door, not
//                        sign-in, and the `redirect` carries this address — token and all — back
//                        here after account creation + verification (or after sign-in).
const acceptInviteRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/invites/accept',
  validateSearch: parseInviteTokenSearch,
  // D-CP-65 — the invite email's destination, whose invitee is a colleague more likely new to
  // Givmo: a signed-out caller is forwarded to the CREATE-ACCOUNT door (see `signedOutDoorFor`),
  // the token riding the preserved `redirect`. Signed in, the page redeems exactly as before.
  staticData: { signedOutDoor: AUTH_ROUTES.signUp },
  component: AcceptInvitePage,
});

// The organization deep link (CPA-BE29) — the ONE address that opens a NAMED organization's console:
//   /organizations/$charityId — a doorway like `/`: refreshes the memberships read, selects the
//                               organization when this person holds it, and forwards to the root
//                               doorway, which runs the landing rule for it; a non-member is forwarded
//                               to the doorway untouched (never into the wizard). The claim-approved
//                               email points here; the backend mirrors the literal. The rule, the
//                               two-step forward, and why the DECISION is made by the layout's probe
//                               rather than here are in `./organization-deep-link.tsx`.
const organizationDeepLinkRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ORGANIZATION_DEEP_LINK_PATH,
  component: OrganizationDeepLinkPage,
});

// ── The Donations module (§2 ruling 2), mounted at `/donations` ────────────────────────────────
//   /donations                   — the §321 statement (the unified four-view table), with the
//                                  getting-started strip above it. CPA-BE16 retired the previous
//                                  tiles + grants table + consenting-donor cards and the knob that
//                                  selected between them, so `GrantsScreen` no longer chooses.
//   /donations/grants/$grantId   — a single grant's drill-down. Still a real, deep-linkable route:
//                                  it REDIRECTS into `/donations?tab=grant&grant=<id>` rather than
//                                  404ing.
//   /donations/payments          — the ACH / mailed-check election + masked profile (§9-9).
//   /donations/team              — the module's Team page, through the ONE `$module/team` route.
// Readable by anyone who holds the module (`donations:read`); the pages fail closed until membership
// resolves. The route family sits behind the direct-link gate (contract mechanic 3): the same answer
// the tile gives, rendered as a page — `locked` with the request door for a Member without the grant
// BEFORE any statement read fires. The one host rule for a reported map without the row lives in the
// console context (§9-14), so every consumer agrees.
function gated(Component: () => ReactNode): () => ReactNode {
  return function GatedDonationsRoute() {
    return (
      <DonationsModuleGate>
        <Component />
      </DonationsModuleGate>
    );
  };
}

const donationsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: DONATIONS_ROUTE,
  // Tolerant, so a stale or hand-edited toolbar link still lands on a working screen.
  validateSearch: validateStatementSearch,
  component: gated(DonationsLanding),
});

const donationsGrantDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: DONATIONS_GRANT_DETAIL_PATH,
  component: gated(GrantDetailScreen),
});

const donationsPaymentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: DONATIONS_PAYMENTS_ROUTE,
  component: gated(PaymentPage),
});

// The three re-homed paths, kept addressable. They render nothing and replace themselves, with the
// statement's search (`?tab/sort/q/chips/grant`) carried across so a shared link still answers the
// question it was sent to answer.
const retiredGrantsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_GRANTS_ROUTE,
  validateSearch: validateStatementSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: DONATIONS_ROUTE, search, replace: true });
  },
});

const retiredGrantDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_GRANT_DETAIL_PATH,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: DONATIONS_GRANT_DETAIL_PATH,
      params: { grantId: params.grantId },
      replace: true,
    });
  },
});

const retiredPaymentRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_PAYMENT_ROUTE,
  beforeLoad: () => {
    throw redirect({ to: DONATIONS_PAYMENTS_ROUTE, replace: true });
  },
});

// Shared Fundraisers module, mounted as four thin routes under the existing shell. The list route
// owns the page's tab word (`?tab=supporters`, FCM-WEB3) through the package's parser; the page shows
// the tab only while the charity flavor carries it, so the word is inert with the knob off.
const fundraisersRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISERS_ROUTES.list,
  validateSearch: parseFundraisersListSearch,
  component: function FundraisersListRoute() {
    const search = fundraisersRoute.useSearch();
    return (
      <CharityFundraisersMount>
        <FundraisersPage view={fundraisersViewFromSearch(search)} />
      </CharityFundraisersMount>
    );
  },
});
const fundraiserCreateRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISERS_ROUTES.create,
  component: () => (
    <CharityFundraisersMount>
      <FundraiserCreatePage />
    </CharityFundraisersMount>
  ),
});
const fundraiserEditorRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_EDITOR_PATH,
  component: function FundraiserEditorRoute() {
    const { fundraiserId } = fundraiserEditorRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} />
      </CharityFundraisersMount>
    );
  },
});
const fundraiserCampaignRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_CAMPAIGN_PATH,
  component: function FundraiserCampaignRoute() {
    const { fundraiserId } = fundraiserCampaignRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} view="campaign" />
      </CharityFundraisersMount>
    );
  },
});
// FCM-WEB25: a campaign's Layers tab. Its reads answer only while the backend serves layers.
const fundraiserLayersRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_LAYERS_PATH,
  component: function FundraiserLayersRoute() {
    const { fundraiserId } = fundraiserLayersRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} view="layers" />
      </CharityFundraisersMount>
    );
  },
});
// FCM-WEB34: a season's setup, opened from its Layers tab. Its reads and its act answer only while the
// backend serves the layers and the pledge engine.
const fundraiserSeasonRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_SEASON_PATH,
  component: function FundraiserSeasonRoute() {
    const { fundraiserId } = fundraiserSeasonRoute.useParams();
    return (
      <CharityFundraisersMount>
        <SeasonSetupPage fundraiserId={fundraiserId} />
      </CharityFundraisersMount>
    );
  },
});
// FCM-WEB49: a fundraiser's Donors tab. Its list answers only while the backend serves it, and the tab
// shows only while the `/org` detail carries `organizer_tools.donor_list`.
const fundraiserDonorsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_DONORS_PATH,
  component: function FundraiserDonorsRoute() {
    const { fundraiserId } = fundraiserDonorsRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} view="donors" />
      </CharityFundraisersMount>
    );
  },
});
// EMC-WEB1: a fundraiser's Emails tab and one email of its series. Their reads answer only while the
// backend serves email campaigns (`EMAIL_CAMPAIGNS_ENABLED`); the tab shows only where the series answers.
const fundraiserEmailsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_EMAILS_PATH,
  component: function FundraiserEmailsRoute() {
    const { fundraiserId } = fundraiserEmailsRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} view="emails" />
      </CharityFundraisersMount>
    );
  },
});
const fundraiserEmailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_EMAIL_PATH,
  validateSearch: parseEmailSearch,
  component: function FundraiserEmailRoute() {
    const { fundraiserId, emailId } = fundraiserEmailRoute.useParams();
    const { step } = fundraiserEmailRoute.useSearch();
    return (
      <CharityFundraisersMount>
        <EmailPage fundraiserId={fundraiserId} emailId={emailId} step={step} />
      </CharityFundraisersMount>
    );
  },
});
// EMC-WEB10: a campaign email's AI designer, Givmo's assistant beside the email's live preview, served while
// `VITE_EMAIL_DESIGNER_ENABLED` is on (its backend gate: `EMAIL_DESIGNER_ASSISTANT_ENABLED`).
const fundraiserEmailDesignRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_EMAIL_DESIGN_PATH,
  component: function FundraiserEmailDesignRoute() {
    const { fundraiserId, emailId } = fundraiserEmailDesignRoute.useParams();
    const orgLogoSrc = useCharityLogoSrc();
    return (
      <CharityFundraisersMount>
        <EmailDesignerPage fundraiserId={fundraiserId} emailId={emailId} orgLogoSrc={orgLogoSrc} />
      </CharityFundraisersMount>
    );
  },
});
// FCM-WEB50: a fundraiser's Messages tab. Its reads and its send answer only while the backend serves
// them, and the tab shows only while the `/org` detail carries `organizer_tools.messages`.
const fundraiserMessagesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_MESSAGES_PATH,
  component: function FundraiserMessagesRoute() {
    const { fundraiserId } = fundraiserMessagesRoute.useParams();
    return (
      <CharityFundraisersMount>
        <FundraiserEditorPage fundraiserId={fundraiserId} view="messages" />
      </CharityFundraisersMount>
    );
  },
});

// The organization QR design editor for one fundraiser (FCM-WEB4), opened from the share panel's
// "Customize". Registered only while `VITE_ORG_QR_DESIGN_ENABLED` is on (below); the editor's logo
// choice draws the charity's own logo off the profile read this host already makes.
const fundraiserQrRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_QR_PATH,
  component: function FundraiserQrRoute() {
    const { fundraiserId } = fundraiserQrRoute.useParams();
    const orgLogoSrc = useCharityLogoSrc();
    return (
      <CharityFundraisersMount>
        <FundraiserQrDesignPage fundraiserId={fundraiserId} orgLogoSrc={orgLogoSrc} />
      </CharityFundraisersMount>
    );
  },
});

// EMC-WEB4: a campaign's Board Ask (frame 21), drafted from the campaign. Board owns the ask; the
// page answers its own not-available where the backend withholds it (BOARD_ASKS_ENABLED).
const fundraiserBoardAskRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISER_BOARD_ASK_PATH,
  component: function FundraiserBoardAskRoute() {
    const { fundraiserId } = fundraiserBoardAskRoute.useParams();
    return (
      <CharityFundraisersMount>
        <BoardAskPage fundraiserId={fundraiserId} />
      </CharityFundraisersMount>
    );
  },
});

// CRM-1 split: the org-generic package mounts at `/crm`; donor analytics stays app-owned.
const contactsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.contacts,
  component: CharityCrmContactsMount,
});

const donorDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.donorPattern,
  component: CharityCrmDonorMount,
});

// CRM-5: the segments, under the same knob as the contacts.
const segmentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.segments,
  component: CharityCrmSegmentsMount,
});

const newSegmentRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.newSegment,
  component: CharityCrmNewSegmentMount,
});

const segmentRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.segmentPattern,
  component: CharityCrmSegmentMount,
});

// CPB-WEB8: "Bring your contacts", the CRM's contact import, inside the CRM mount.
const crmImportRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: CRM_MODULE_ROUTES.import,
  component: () => <CharityCrmImportMount />,
});

const analyticsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ANALYTICS_ROUTES.dashboard,
  component: AnalyticsPage,
});

// RETIRED — the page's address before it joined the Donations family. Registered only with the page
// (the same knob), so a dark build still answers not-found there.
const retiredAnalyticsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ANALYTICS_ROUTES.retired,
  beforeLoad: () => {
    throw redirect({ to: ANALYTICS_ROUTES.dashboard, replace: true });
  },
});

// The Compliance module (CCM-WEB1; mounted at `/compliance` by CCM-MOD1B; re-composed as Calendar ·
// Filings · Documents at CCM-R1-INTAKE (a), set C Q1) — compiled dark behind
// VITE_COMPLIANCE_UI_ENABLED, every page behind the ONE mount (`CharityComplianceMount`: the
// direct-link gate over the console's standing read, then the console-fed host):
//   /compliance                     — the Calendar, the module's landing.
//   /compliance/filings             — Filings: every filing the organization makes, by family.
//   /compliance/filings/$filingId   — Filings with that filing's panel open.
//   /compliance/filings/$filingId/rule — the same panel showing the rule behind the filing (D2).
//   /compliance/documents           — the documents kept for the filings.
//   /compliance/review              — the review: what Givmo found, and the decisions on it
//                                     (CCM-R1-REVIEW-WEB), at the hidden queue's old address.
//   /compliance/$obligationId       — the old obligation page's address: redirects to its panel (D2).
//   /compliance/team                — the shell's Team page, as a RESERVED static alias (below).
//   /compliance/sign-in             — the module's door, as a RESERVED static alias (the root area).
//
// ⚠ RANKING, not registration order, keeps `filings`, `documents`, `review`, the two retired module
// addresses and the two aliases out of `$obligationId`: TanStack ranks a static segment above a
// dynamic one whatever the order below, so the literal children are listed first for READING only,
// and `filings/$filingId` is two segments where `$obligationId` is one. The pins are
// `compliance-redirects.test.tsx` (each literal lands where it should) and
// `compliance-mount.test.tsx` (the aliases resolve to the door and the Team page, the panel's
// address to Filings). The aliases exist because a static FIRST segment outranks `$module/…`:
// without them `/compliance/team` and `/compliance/sign-in` would resolve to an obligation named
// "team".
const complianceCalendarRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_ROUTES.calendar,
  component: ComplianceCalendarPage,
});

const complianceFilingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_ROUTES.filings,
  component: ComplianceFilingsPage,
});

const complianceFilingPanelRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FILING_PANEL_PATH,
  component: ComplianceFilingsPage,
});

const complianceFilingRuleRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FILING_RULE_PATH,
  component: ComplianceFilingRulePage,
});

// CCM-R1-DOCS: the filing's form, as Givmo prepares it (slice 2 §5.1). A page of its own, not a
// sheet over Filings; Filings stays lit in the sidebar.
const complianceFilingFormRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FILING_FORM_PATH,
  component: ComplianceFilingFormPage,
});

// CCM-R1-SUBMIT-WEB: the signed filing's send review (slice 3 §5), a page of its own beside the form;
// Filings stays lit in the sidebar.
const complianceFilingSendRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FILING_SEND_PATH,
  component: ComplianceFilingSendPage,
});

const complianceDocumentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_ROUTES.documents,
  component: ComplianceDocumentsPage,
});

// CCM-R1-REVIEW-WEB: the review (D-CCM-51), a page of its own outside the navigation, at the address
// the hidden review queue held (A-C10): the Calendar's card opens it.
const complianceReviewRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_ROUTES.review,
  component: ComplianceReviewPage,
});

// CCM-R1-SIGN-WEB: a request someone was asked to sign, at the exact address the backend's signing
// emails link to (`/compliance/sign/{request_id}`). A static first segment, so it doesn't collide
// with the flat `$obligationId` below. Signing is always the person pressing Sign: no AI signs, so
// there is no approve-for-AI page (D-CCM-31).
const complianceSigningRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: SIGNING_PATH,
  component: ComplianceSigningPage,
});

// The old obligation page's address (design go D2, approved by Jonathan): a bookmark, a shared link
// or a mail naming `/compliance/<id>` opens that filing's panel, replace-redirected with its search
// and hash. The rule's own view, which kept "Laws change; re-read the source at filing time.", is
// the panel's rule view now (`/compliance/filings/<id>/rule`), one click from its citation.
const complianceObligationRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: OBLIGATION_DETAIL_PATH,
  beforeLoad: ({ params, search, location }) => {
    throw redirect({
      to: FILING_PANEL_PATH,
      params: { filingId: params.obligationId },
      search,
      hash: location.hash,
      replace: true,
    });
  },
});

// Reserved path alias for the same inherited Team page, ahead of $obligationId.
const complianceTeamRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_ROUTES.team,
  component: () => <ModuleTeamRoute segment={COMPLIANCE_SLUG} />,
});

// Retired addresses, kept addressable as replace-redirects. Each carries its search and its HASH
// across — `hash` is passed explicitly off `location`, since a redirect drops the fragment otherwise
// — and the drill-down its `$obligationId` verbatim, so a bookmark, a shared link or a mail still
// lands on the entry it named. `replace: true` states the intent; router-core commits every
// `beforeLoad` redirect as a replace regardless (`load()`), and `compliance-redirects.test.tsx` pins
// the behaviour (Back from the module returns to the page before the retired address).
function retiredComplianceRedirect(
  to: string,
): (args: { search: Record<string, unknown>; location: { hash: string } }) => never {
  return ({ search, location }) => {
    throw redirect({ to, search, hash: location.hash, replace: true });
  };
}

// The module's own two retired addresses (set C Q1): the Dates page the Calendar replaces and the
// recorded facts (back with the fact store, D-CCM-14). Each lands on the Calendar; no page is
// registered at either. The review queue's address is the review's own (CCM-R1-REVIEW-WEB).
const retiredComplianceDeadlinesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_RETIRED_ROUTES.deadlines,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.calendar),
});

const retiredComplianceFactsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: COMPLIANCE_RETIRED_ROUTES.recordedFacts,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.calendar),
});

// The six retired `/filing-register*` addresses (the Phase-2 surface, CCM-MOD1B), each pointed
// DIRECTLY at its final home so none takes two hops: the register and two of its hidden pages land
// on the Calendar, its review queue on the review (CCM-R1-REVIEW-LEGACY-RETIRE), the documents on
// Documents, and an entry on its filing's panel. Reminder emails sent before CCM-REMIND-LINK-ORG
// link `/filing-register`, so that redirect stays: those emails still open the Calendar. Newer
// reminders link the organization's deep link to `/compliance`.
const retiredFilingRegisterRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_ROUTE,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.calendar),
});

const retiredFilingRegisterReviewRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_REVIEW_ROUTE,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.review),
});

const retiredFilingRegisterDeadlinesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_DEADLINES_ROUTE,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.calendar),
});

const retiredFilingRegisterFactsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_FACTS_ROUTE,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.calendar),
});

const retiredFilingRegisterDocumentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_DOCUMENTS_ROUTE,
  beforeLoad: retiredComplianceRedirect(COMPLIANCE_ROUTES.documents),
});

const retiredFilingRegisterObligationRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_FILING_REGISTER_OBLIGATION_PATH,
  beforeLoad: ({ params, search, location }) => {
    throw redirect({
      to: FILING_PANEL_PATH,
      params: { filingId: params.obligationId },
      search,
      hash: location.hash,
      replace: true,
    });
  },
});

// Back-office setup (CBO-IN3) — compiled dark behind VITE_ENROLLMENT_UI_ENABLED:
//   /enrollment                 — the organization's live record, or the way to start one.
//   /enrollment/$enrollmentId   — one record: its details, what it is waiting on, its event log.
//
// ⚠ The literal `/enrollment` is registered BEFORE the `$enrollmentId` param route, or the literal
// is swallowed as a record id.
//
// Unlike every other flag-gated family here, this one reveals NO nav item — setting an organization
// up is a once-ever act, and D-CP-36 retired the Claim group on exactly that reasoning. Its door is
// an Owner-only row on the organization's own page (`/org`, slice 3 of CPA-WEB40); `app-shell.tsx`
// titles the route instead.
const enrollmentRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ENROLLMENT_ROUTES.home,
  component: EnrollmentPage,
});

const enrollmentRecordRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ENROLLMENT_RECORD_PATH,
  component: function EnrollmentRecordRouteComponent() {
    const { enrollmentId } = enrollmentRecordRoute.useParams();
    return <EnrollmentRecordPage enrollmentId={enrollmentId} />;
  },
});

// The intake doors (CBO-IN4) — where history is brought into books that already exist. A deeper
// literal under the same `$enrollmentId` param, so `app-shell.tsx` titles it with no diff at all.
const enrollmentBackfillRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ENROLLMENT_BACKFILL_PATH,
  component: function EnrollmentBackfillRouteComponent() {
    const { enrollmentId } = enrollmentBackfillRoute.useParams();
    return <EnrollmentBackfillPage enrollmentId={enrollmentId} />;
  },
});

// Served Charity Terms (CPA-WEB3 residual). Owner and Member can read; the page owns the fail-closed
// Owner-only acceptance gate. Always registered: its compile knob was retired by CPA-WEB67 once the
// signed instrument was effective and the knob read `true` in every environment.
const termsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/terms',
  // Keep the presenter off the portal's initial bundle; it is loaded only after the route matches.
  component: lazyRouteComponent(() => import('../features/terms/terms-page'), 'TermsPage'),
});

// Profile editor (CPA-WEB6):
//   /profile — the charity's public profile (mission / website / contact / logo, Owner-editable) + the
//              READ-ONLY verification domain guard: the CPA-WEB6 editor while
//              `VITE_PROFILE_PAGE_ENABLED` is unset — the same tree, gates, ids, states and pixels
//              as before the move, plus one wrapper element carrying the panel's testid
//              (Profile Enrichment's live-route invariant, `profile-route.test.tsx`). With the knob
//              set, the SAME address renders the tabbed
//              Profile page (PE-WEB1: Details · Page content · View as public), the tab mirrored to
//              `?tab=`. The knob is read per render, not at module load, so both answers are
//              testable without re-instantiating the tree; the search parser is inert while dark.
function ProfileRoute() {
  return isProfilePageEnabled() ? <CharityProfileRoute /> : <ProfilePage />;
}
const profileRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/profile',
  validateSearch: parseProfileSearch,
  component: ProfileRoute,
});

// The Public tab's QR design page (PE-WEB11): the QR card's "Customize". Registered only while BOTH
// the Profile page and the organization QR design are revealed (below), so the live CPA-WEB6 editor
// at /profile stays exactly as it is with either knob unset.
const profileQrRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: PROFILE_ROUTES.qr,
  component: CharityProfileQrRoute,
});

// The catalog-removal request page (CPA-WEB7; re-addressed to `/removal` and retitled "Remove from
// Givmo" at Org Machinery OM-17's cut-over, RULED 2026-09-07):
//   /removal — the request-to-be-removed flow (Owner-only request, confirm-first with honest
//              "removal doesn't affect payouts" consequences copy) + the pending-request status when
//              one is open. Its content, its ids (`settings-removal-*`, the root `page-settings`) and
//              its Owner gate are UNCHANGED; the composed settings page's removal card is a DOOR into
//              it, and the address is read off `SETTINGS_ROUTES.removal`, never spelled here.
const removalRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: SETTINGS_ROUTES.removal,
  component: SettingsPage,
});

// The Admin Console (CPA-WEB40 · shell contract v3) — the organization's OWN pages, each gated on
// the CAPABILITY it declares (`features/org/org-pages.ts`; `RequireCapability` replace-redirects a
// SETTLED list lacking it to `/apps`; an unreported list renders):
//   /org              — Apps: the always-on Donations card with no switch, the catalog modules with
//                       their org-wide switches, the pending strip.
//   /org/team-access  — the roster + per-module access editor, the invites, the pending requests,
//                       the connected AIs (the ONE order every Team page wears). `?module=` narrows
//                       the roster.
//   /org/activity     — the access event log, its own page.
//   /team-access      — RETIRED: the CBO-CT1 slice-6 path, kept as a replace-redirect with its search.
// A dark backend (`ORG_CONSOLE_ENABLED` unset) renders the package's calm not-available notices —
// never an error.
function validateTeamAccessSearch(search: Record<string, unknown>): { module?: string } {
  return typeof search.module === 'string' && search.module.length > 0
    ? { module: search.module }
    : {};
}

const orgRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ORG_ROUTE,
  component: function OrgRoute() {
    return (
      <RequireCapability capability={CAPABILITY_WRITE_SETTINGS}>
        <OrgPage />
      </RequireCapability>
    );
  },
});

const orgTeamAccessRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: TEAM_ACCESS_ROUTE,
  validateSearch: validateTeamAccessSearch,
  component: function OrgTeamAccessRoute() {
    const { module } = orgTeamAccessRoute.useSearch();
    return (
      <RequireCapability capability={CAPABILITY_WRITE_MEMBER_ACCESS}>
        <TeamAccessPage {...(module === undefined ? {} : { module })} />
      </RequireCapability>
    );
  },
});

const activityRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ACTIVITY_ROUTE,
  component: function ActivityRoute() {
    return (
      <RequireCapability capability={CAPABILITY_READ_EVENTS}>
        <ActivityPage />
      </RequireCapability>
    );
  },
});

// The gear's ONE composed settings page — any admin of the organization (T2.1; composed at
// D-CPO-33). At `/settings` since the OM-17 cut-over — the same literal on both hosts, pinned in
// `@givmo/org-console`'s `settings-address.test.ts`; the catalog-removal request page that held the
// address answers `/removal` (above). Its address carries where it was opened from
// (`?from=…&frame=…`, Org Machinery OM-28 — `console/settings-origin.ts`), so a reload or a new tab
// opens the same page.
const settingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: SETTINGS_ROUTE,
  validateSearch: parseSettingsOriginSearch,
  component: ConsoleSettingsPage,
});

// `/preferences` — the composed settings page's address before the cut-over (in people's history and
// in the OM-14 e2e specs). Replaces itself, like every retired path here, carrying the FRAGMENT (a
// `/preferences#notifications` bookmark lands on its card) and the HISTORY STATE (a Back into an
// entry an earlier build wrote with `settingsOrigin` re-enters that origin's frame) — Org Machinery
// OM-17-R1.
const retiredPreferencesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_PREFERENCES_ROUTE,
  beforeLoad: ({ location }) => {
    throw redirect({
      to: SETTINGS_ROUTE,
      hash: location.hash,
      state: location.state,
      replace: true,
    });
  },
});

// ── Every mounted module's Team page (T6.7) ────────────────────────────────────────────────────
// ── Books: one route per page beneath its mount, over the shared page (BM-WEB2) ─────────────────
const booksRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.overview,
  component: () => <CharityBooksMount view={{ page: 'overview' }} />,
});
const booksStatementsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.statements,
  component: () => <CharityBooksMount view={{ page: 'statements', statement: null }} />,
});
const booksStatementRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.statement,
  component: function BooksStatementRoute() {
    const { statement } = booksStatementRoute.useParams();
    return <CharityBooksMount view={{ page: 'statements', statement }} />;
  },
});
const booksLedgerRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.ledger,
  component: () => <CharityBooksMount view={{ page: 'ledger' }} />,
});
const booksJournalRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.journal,
  component: () => <CharityBooksMount view={{ page: 'journal' }} />,
});
const booksAccountRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.account,
  component: function BooksAccountRoute() {
    const { accountCode } = booksAccountRoute.useParams();
    return <CharityBooksMount view={{ page: 'account', accountCode }} />;
  },
});
const booksReconciliationRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.reconciliation,
  component: () => <CharityBooksMount view={{ page: 'reconciliation' }} />,
});
const booksExportRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOOKS_ROUTES.export,
  component: () => <CharityBooksMount view={{ page: 'export' }} />,
});
const booksRoutes = [
  booksRoute,
  booksStatementsRoute,
  booksStatementRoute,
  booksLedgerRoute,
  booksJournalRoute,
  booksAccountRoute,
  booksReconciliationRoute,
  booksExportRoute,
];

// Reserved path alias for the same inherited Team page, ahead of $fundraiserId.
const fundraisersTeamRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: FUNDRAISERS_ROUTES.team,
  component: () => <ModuleTeamRoute segment={FUNDRAISERS_SLUG} />,
});

const moduleTeamRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '$module/team',
  component: function ModuleTeamRouteComponent() {
    const { module } = moduleTeamRoute.useParams();
    return <ModuleTeamRoute segment={module} />;
  },
});

const retiredTeamAccessRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: RETIRED_TEAM_ACCESS_ROUTE,
  validateSearch: validateTeamAccessSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: TEAM_ACCESS_ROUTE, search, replace: true });
  },
});

// Support (EP-FIX7): the static contact surface the portal's three "contact support" affordances
// point at (the account menu's Help & support row, org-search no-match guidance, claim-wizard
// not-claimable panel). Like /terms it carries no sidebar nav entry — `activeTitle` names it instead.
// It is exempt from ClaimFirstGate (see CLAIM_FIRST_PATHS): two of its three entry points are shown
// precisely to callers who have NO membership yet, so a claim-gated /support would bounce them back.
const supportRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/support',
  component: SupportPage,
});

// The not-found surface, inside the ONE layout. Static and parameterized routes above rank ahead of
// the splat, so `/$module/sign-in` and `/$module/team` keep their own answers — and every
// compiled-dark family whose knob is off (`/fundraisers`, `/crm`, `/compliance` and its retired
// `/filing-register` addresses, `/enrollment`) falls through to it exactly as before, now wearing
// the shell.
const notFoundRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '$',
  component: NotFound,
});

// CBG-MOD1: one shared Board build; the director tree never enters the officer guard.
//
// Meetings lives at `/board/meetings`, not at the module's root, so its sidebar row is an ordinary
// segment-prefix row that stays lit on every meeting's own pages (Jonathan, 2026-09-28) — the
// shell's family root matches exactly, which is what left a meeting page with no row lit. The
// root itself is Board home: it replaces itself with Meetings (`boardHomeRoute`, below).
const boardMeetingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/meetings',
  component: () => (
    <CharityBoardMount>
      <SharedBoardMeetingsPage />
    </CharityBoardMount>
  ),
});
const boardMeetingDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/meetings/$meetingId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardMeetingDetailPage />
    </CharityBoardMount>
  ),
});
const boardPacketRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/meetings/$meetingId/packet',
  component: () => (
    <CharityBoardMount>
      <SharedBoardPacketPage />
    </CharityBoardMount>
  ),
});
const boardInMeetingRecordRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/meetings/$meetingId/record',
  component: () => (
    <CharityBoardMount>
      <SharedBoardInMeetingRecordPage />
    </CharityBoardMount>
  ),
});
const boardMinutesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/meetings/$meetingId/minutes',
  component: () => (
    <CharityBoardMount>
      <SharedBoardMinutesPage />
    </CharityBoardMount>
  ),
});
const boardRosterRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/roster',
  component: () => (
    <CharityBoardMount>
      <SharedBoardRosterPage />
    </CharityBoardMount>
  ),
});
const boardMemberDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/roster/$memberId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardMemberDetailPage />
    </CharityBoardMount>
  ),
});
// CBG-YR6-REG: the board's committees, after Board members (D-CBG-9 amended), and one committee
// beneath them, so the Committees row stays lit there.
const boardCommitteesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/committees',
  component: () => (
    <CharityBoardMount>
      <SharedBoardCommitteesPage />
    </CharityBoardMount>
  ),
});
const boardCommitteeRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/committees/$committeeId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardCommitteePage />
    </CharityBoardMount>
  ),
});
// CBG-YR4-ASSESS-WEB: the board's self-assessment, `?assessment=<id>` naming one; a page of its own,
// so its row is lit there. It shows as Self-assessment (CBG-UX8-MENU). Its compensation decisions
// moved to the Form 990 page as that page's second part (D-CBG-35 (1)), so their old address, this
// page's `?tab=compensation`, replaces onto that part, keeping any other search and the fragment;
// every other address here stays the self-assessment's.
const boardEvaluationsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/evaluations',
  beforeLoad: ({ location }) => {
    const search = location.search as Record<string, unknown>;
    if (search.tab !== BOARD_RETIRED_COMPENSATION_TAB) return;
    throw redirect({
      to: '/board/form-990',
      search: { ...search, tab: BOARD_FORM_990_TABS.compensation },
      hash: location.hash,
      replace: true,
    });
  },
  component: () => (
    <CharityBoardMount>
      <SharedBoardEvaluationsPage />
    </CharityBoardMount>
  ),
});
// CBG-YR5-VIEW: the Form 990 page, the last row of Board's list (D-CBG-9 amended) — one tax year of
// Part VI, `?tax_year_ends_on=` naming it (and, with no fiscal year end recorded,
// `?year_end_month=&year_end_day=`), and since CBG-UX8-MENU its second part, the compensation
// decisions (`?tab=compensation`); the page reads its own search.
const boardForm990Route = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/form-990',
  component: () => (
    <CharityBoardMount>
      <SharedBoardForm990Page />
    </CharityBoardMount>
  ),
});
// CBG-YR5-SCHO: the Schedule O draft, beneath the Form 990 page (its row stays lit there), carrying
// that page's address; the page reads its own search.
const boardScheduleORoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/form-990/schedule-o',
  component: () => (
    <CharityBoardMount>
      <SharedBoardScheduleOPage />
    </CharityBoardMount>
  ),
});
// CBG-YR5-DIST and CBG-YR5-REVIEW: one copy of the return provided to the board, its receipts and
// the board's review, beneath the Form 990 page (its row stays lit there).
const boardReturnReceiptsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/form-990/returns/$recordId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardReturnReceiptsPage />
    </CharityBoardMount>
  ),
});
// EMC-WEB4: the Board Asks (no nav row: the approved frames reach them from the to-do list and the
// emails), and one ask: approve and send it while it waits, who's taking part once it's sent.
const boardAsksRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/asks',
  component: () => (
    <CharityBoardMount>
      <SharedBoardAsksPage />
    </CharityBoardMount>
  ),
});
const boardAskRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/asks/$askId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardAskPage />
    </CharityBoardMount>
  ),
});
// D-CBG-9 (2026-09-28): governing documents and governance parameters are the two parts of ONE
// Governance page, and Board's settings are the Board section of the Settings page (since CBG-UX14,
// its "Calendar" card alone: the director sign-in policy retired with D-CBG-20, and record retention
// and legal holds left Settings with D-CBG-53). Every old address still answers, as a replace-redirect
// to its new home (a pushed redirect would leave a back-button trap): the two governance addresses
// land on their own part, keeping any search and fragment; the two election pages land on the
// Settings page opened from Board, so its Board section and its frame are Board's.
const boardGovernanceRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/governance',
  component: () => (
    <CharityBoardMount>
      <SharedBoardGovernancePage />
    </CharityBoardMount>
  ),
});
const boardParametersRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/parameters',
  beforeLoad: ({ location }) => {
    throw redirect({
      to: '/board/governance',
      search: { ...location.search, tab: BOARD_GOVERNANCE_TABS.meetingRules },
      hash: location.hash,
      replace: true,
    });
  },
});
/** The Settings page opened from Board: its Board section, in Board's frame (the origin in its address). */
function boardSettingsRedirect(): never {
  throw redirect({
    to: SETTINGS_ROUTE,
    search: settingsOriginSearchFor(BOARD_ROUTE, BOARD_SLUG),
    replace: true,
  });
}
// The retired sign-in choice's old address (D-CBG-20: every board has one rule) and the retention
// and holds page's (D-CBG-53: no officer screen sets either since 2026-11-01): an old link still lands
// on Board's settings, which show the calendar card while the calendar is served and nothing of
// Board's while it is dark (the section hidden with it).
const boardDirectorAccessRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/director-access',
  beforeLoad: boardSettingsRedirect,
});
const boardRetentionRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/retention',
  beforeLoad: boardSettingsRedirect,
});
const boardHoldReportRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/legal-holds/$holdId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardHoldReportPage />
    </CharityBoardMount>
  ),
});
const boardConsentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/consents',
  component: () => (
    <CharityBoardMount>
      <SharedBoardConsentsPage />
    </CharityBoardMount>
  ),
});
const boardConsentDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/consents/$consentId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardConsentDetailPage />
    </CharityBoardMount>
  ),
});
const boardConflictsRegisterRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/conflicts',
  component: () => (
    <CharityBoardMount>
      <SharedBoardConflictsRegisterPage />
    </CharityBoardMount>
  ),
});
// CBG-YR2-WEB1: one person's answers for a year, beneath the Conflicts of interest page (its row
// stays lit there). Dark like the page's own year: the response read's route miss is its answer.
const boardCoiResponseRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/conflicts/annual/$cycleId/responses/$recordId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardCoiResponsePage />
    </CharityBoardMount>
  ),
});
const boardRecordsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/records',
  component: () => (
    <CharityBoardMount>
      <SharedBoardRecordsPage />
    </CharityBoardMount>
  ),
});
const boardRecordDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/records/$recordId',
  component: () => (
    <CharityBoardMount>
      <SharedBoardRecordDetailPage />
    </CharityBoardMount>
  ),
});
const boardGoverningDocumentsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/governing-documents',
  beforeLoad: ({ location }) => {
    throw redirect({
      to: '/board/governance',
      search: { ...location.search, tab: BOARD_GOVERNANCE_TABS.documents },
      hash: location.hash,
      replace: true,
    });
  },
});
// The workspace's two addresses are the package's constants: this app's copy rail reads every string
// literal here, and the word their path carries is one it keeps out of the charity's copy.
const boardImportBatchesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOARD_IMPORT_ROUTES.batches,
  component: () => (
    <CharityBoardMount>
      <SharedBoardImportBatchesPage />
    </CharityBoardMount>
  ),
});
const boardImportBatchRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOARD_IMPORT_ROUTES.batch,
  component: () => (
    <CharityBoardMount>
      <SharedBoardImportBatchPage />
    </CharityBoardMount>
  ),
});
// CBG-AI5-PROPOSE: a batch of past records the connected AI proposed, and its one confirmation.
// The connector's answers link here (`review_url`).
const boardProposedBatchRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOARD_IMPORT_ROUTES.proposedBatch,
  component: () => (
    <CharityBoardMount>
      <SharedBoardProposedBatchPage />
    </CharityBoardMount>
  ),
});
// CBG-AI5-INBOX-WEB: a folder a person uploads for their AI to read through the connector.
const boardImportUploadsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: BOARD_IMPORT_ROUTES.uploads,
  component: () => (
    <CharityBoardMount>
      <SharedBoardImportUploadsPage />
    </CharityBoardMount>
  ),
});
// ── The director family (CBG-SEC2 SEC2-WEB; contract §2.1) ─────────────────────────────────────
//
// Directors sign in with Givmo accounts (D-CBG-16). The family is its own tree off the root, never
// the officer's authenticated layout: no memberships read, no portal-entry gate, no console shell.
// Its host renders the shared "Confirm it's you" sheet's host and Board's director host around
// every page; its guards are `features/board/director-guard.ts`.
const boardDirectorFamilyRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'board-director',
  component: function BoardDirectorFamily() {
    return (
      <CharityDirectorHost>
        <Outlet />
      </CharityDirectorHost>
    );
  },
});
// The invitation: opening it spends nothing, so it opens signed out.
const boardDirectorInvitationRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.invitation,
  // The one link format (CBG-FIX14): the token, and a packet distribution's version beside it.
  validateSearch: directorRedeemSearch,
  beforeLoad: confirmDirectorEmailFirst,
  component: SharedBoardDirectorInvitationPage,
});
// CBG-SEC2-L4-MOVE (D-CBG-23 flow (b)): a director moves their own seat to another Givmo account.
// Like the invitation it opens signed out, since its middle step is the other account's sign-in;
// a signed-in, unconfirmed account confirms its address first, as on the invitation.
const boardDirectorMoveRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.move,
  beforeLoad: confirmDirectorEmailFirst,
  component: SharedBoardDirectorMovePage,
});
// The director door, where the family's guard sends a signed-out visitor.
const boardDirectorSignInRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.signIn,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectSignedInDirector,
  component: SharedBoardDirectorDoorPage,
});
// The console's own email screens, in their director arrival (contract §4.3).
const boardDirectorSignInEmailRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.signInEmail,
  validateSearch: parseDirectorEmailSearch,
  beforeLoad: redirectSignedInDirector,
  component: () => <SignInScreen arrival="director" />,
});
// The way back in, in Board's register (AS-WEB26; cut 1007e's fold, Board's B-F3): every director
// side "Lost access to your company sign-in?" opens it. Signed out by nature, so a signed-in arrival
// goes to the director's boards. `back` is the door to return to (sanitized).
const boardDirectorLostCompanyAccessRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.lostCompanyAccess,
  validateSearch: parseBackSearch,
  beforeLoad: ({ context, location }) =>
    redirectSignedInDirector({ context, location, search: {} }),
  component: () => <LostCompanyAccessScreen arrival="director" />,
});
// The console's organization sign-in step, in its director arrival (AS-WEB30, Board's §6 (3)).
const boardDirectorOrganizationSsoRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.organizationSso,
  validateSearch: parseRedirectSearch,
  beforeLoad: redirectSignedInDirector,
  component: () => <OrganizationSsoScreen arrival="director" />,
});
const boardDirectorSignUpEmailRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.signUpEmail,
  validateSearch: parseDirectorEmailSearch,
  beforeLoad: redirectSignedInDirector,
  component: () => <SignUpEmailScreen arrival="director" />,
});
const boardDirectorConfirmEmailRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  path: DIRECTOR_ROUTES.confirmEmail,
  validateSearch: parseVerifyEmailSearch,
  beforeLoad: requireDirectorConfirmation,
  component: () => <VerifyEmailScreen arrival="director" />,
});
// Every board page: an account, confirmed where its sign-in can be repaired by the emailed code.
const boardDirectorSignedInRoute = createRoute({
  getParentRoute: () => boardDirectorFamilyRoute,
  id: 'board-director-signed-in',
  beforeLoad: requireDirectorAccount,
});
const boardDirectorBoardsRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: DIRECTOR_ROUTES.home,
  component: SharedBoardDirectorBoardsPage,
});
const boardDirectorMeetingsRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/meetings',
  component: SharedBoardDirectorMeetingsPage,
});
const boardDirectorMeetingDetailRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/meetings/$meetingId',
  component: SharedBoardDirectorMeetingDetailPage,
});
const boardDirectorConsentsRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/consents',
  component: SharedBoardDirectorConsentsPage,
});
const boardDirectorConsentDetailRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/consents/$consentId',
  component: SharedBoardDirectorConsentDetailPage,
});
const boardDirectorDisclosuresRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/disclosures',
  component: SharedBoardDirectorDisclosuresPage,
});
// CBG-YR2-WEB2: the year's own questionnaire, `?org=&cycle=`. ⚠ The backend's request mail spells this
// address whole (`BOARD_DIRECTOR_ANNUAL_DISCLOSURE_PATH`): change both sides in one round, never one.
const boardDirectorAnnualDisclosureRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/disclosures/annual',
  component: SharedBoardDirectorAnnualDisclosurePage,
});
// The account's own "Sign-in & security" (Account Security's card): a director who isn't one of the
// organization's officers has no Settings to reach it through.
const boardDirectorSignInSecurityRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/sign-in-security',
  component: SharedBoardDirectorSignInSecurityPage,
});
const boardDirectorPacketRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/packets/$packetId',
  component: SharedBoardDirectorPacketPage,
});
const boardDirectorRecordsRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/records',
  component: SharedBoardDirectorRecordsPage,
});
const boardDirectorRecordDetailRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/records/$recordId',
  component: SharedBoardDirectorRecordDetailPage,
});
// CBG-YR4-START: the member's own getting-started list; CBG-YR4-ABOUT: About the board.
const boardDirectorGettingStartedRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/getting-started',
  component: SharedBoardDirectorGettingStartedPage,
});
const boardDirectorAboutRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/about',
  component: SharedBoardDirectorAboutPage,
});
// CBG-UX10 (D-CBG-40): Board members, and the committees the seat sits on, as a director reads them.
const boardDirectorMembersRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/members',
  component: SharedBoardDirectorMembersPage,
});
const boardDirectorCommitteesRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/committees',
  component: SharedBoardDirectorCommitteesPage,
});
// CBG-YR4-ASSESS-WEB: the board's self-assessment as the person answers it, `?org=&assessment=`.
// ⚠ The backend's ask and reminder mails spell this address whole
// (`BOARD_DIRECTOR_SELF_ASSESSMENT_PATH`): change both sides in one round, never one.
const boardDirectorSelfAssessmentRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/self-assessment',
  component: SharedBoardDirectorSelfAssessmentPage,
});
// CBG-YR5-DIST: a copy of the return provided to the board, as the person reads it, `?org=`.
// ⚠ The backend's mail spells this address whole (`BOARD_DIRECTOR_RETURN_PATH`): change both sides
// in one round, never one.
const boardDirectorReturnRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/returns/$recordId',
  component: SharedBoardDirectorReturnPage,
});
// EMC-WEB4: a Board Ask sent to the seat, `?org=`.
// ⚠ The backend's mail spells this address whole (`BOARD_DIRECTOR_ASK_PATH`): change both sides in
// one round, never one.
const boardDirectorAskRoute = createRoute({
  getParentRoute: () => boardDirectorSignedInRoute,
  path: '/board/director/asks/$askId',
  component: SharedBoardDirectorAskPage,
});
// CBG-CAL1 slice 1d: where Google sends a calendar connect back (`<Board mount>/calendar/connected`).
// ⚠ This exact address, on each console host and environment, is what the operator registers with
// Google and in core's ORG_INTEGRATIONS_OAUTH_REDIRECT_URIS (CBG-CAL2), and core matches it exactly:
// changing it is an operator change in every environment. The page finishes the flow, so it sits in
// the officer guard; in a pop-up it closes itself as soon as the dialog that asked takes the answer.
const boardCalendarConnectedRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: `${BOARD_ROUTE}${BOARD_CALENDAR_CONNECT_RETURN_PAGE}`,
  component: () => (
    <CharityBoardMount>
      <SharedBoardCalendarConnectReturnPage />
    </CharityBoardMount>
  ),
});
/**
 * Board home — every address that names the module (a tile, a sign-in landing, a saved link) —
 * replaced by Meetings, carrying the address's search and hash and the frame it was opened with
 * (a sign-in landing's explicit none, OM-18-R1).
 */
const boardHomeRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board',
  beforeLoad: ({ location }) => {
    const frame = sidebarFrameOf(location.state);
    throw redirect({
      to: '/board/meetings',
      search: location.search,
      hash: location.hash,
      replace: true,
      ...(frame === undefined ? {} : { state: { sidebarFrame: frame } }),
    });
  },
});
const boardTeamRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/board/team',
  component: () => <ModuleTeamRoute segment={BOARD_SLUG} />,
});
const boardSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/board/sign-in',
  validateSearch: parseRedirectSearch,
  beforeLoad: ({ context, search }) =>
    beforeModuleSignIn({ context, search, params: { module: BOARD_SLUG } }),
  component: () => <ModuleSignInPage segment={BOARD_SLUG} />,
});

export const routeTree = rootRoute.addChildren([
  ...(isBoardUiEnabled()
    ? [
        boardDirectorFamilyRoute.addChildren([
          boardDirectorInvitationRoute,
          boardDirectorMoveRoute,
          boardDirectorSignInRoute,
          boardDirectorSignInEmailRoute,
          boardDirectorOrganizationSsoRoute,
          boardDirectorLostCompanyAccessRoute,
          boardDirectorSignUpEmailRoute,
          boardDirectorConfirmEmailRoute,
          boardDirectorSignedInRoute.addChildren([
            boardDirectorBoardsRoute,
            boardDirectorMeetingsRoute,
            boardDirectorMeetingDetailRoute,
            boardDirectorConsentsRoute,
            boardDirectorConsentDetailRoute,
            boardDirectorDisclosuresRoute,
            boardDirectorAnnualDisclosureRoute,
            boardDirectorSignInSecurityRoute,
            boardDirectorPacketRoute,
            boardDirectorRecordsRoute,
            boardDirectorRecordDetailRoute,
            boardDirectorGettingStartedRoute,
            boardDirectorAboutRoute,
            boardDirectorMembersRoute,
            boardDirectorCommitteesRoute,
            boardDirectorSelfAssessmentRoute,
            boardDirectorReturnRoute,
            boardDirectorAskRoute,
          ]),
        ]),
        boardSignInRoute,
      ]
    : []),
  loginHomeRoute,
  signInRoute,
  signUpRoute,
  signUpEmailRoute,
  verifyEmailRoute,
  lostCompanyAccessRoute,
  organizationSsoRoute,
  organizationSsoCallbackRoute,
  moduleSignInRoute,
  ...(isFundraisersModuleEnabled() ? [fundraisersSignInRoute] : []),
  ...(isComplianceUiEnabled() ? [complianceSignInRoute] : []),
  authenticatedLayoutRoute.addChildren([
    ...(isBoardUiEnabled()
      ? [
          boardMeetingsRoute,
          boardMeetingDetailRoute,
          boardPacketRoute,
          boardInMeetingRecordRoute,
          boardMinutesRoute,
          boardRosterRoute,
          boardMemberDetailRoute,
          boardCommitteesRoute,
          boardCommitteeRoute,
          boardEvaluationsRoute,
          boardParametersRoute,
          boardDirectorAccessRoute,
          boardRetentionRoute,
          boardHoldReportRoute,
          boardConsentsRoute,
          boardConsentDetailRoute,
          boardConflictsRegisterRoute,
          boardCoiResponseRoute,
          boardRecordsRoute,
          boardRecordDetailRoute,
          boardGovernanceRoute,
          boardForm990Route,
          boardScheduleORoute,
          boardReturnReceiptsRoute,
          boardAsksRoute,
          boardAskRoute,
          boardGoverningDocumentsRoute,
          boardImportBatchesRoute,
          boardImportBatchRoute,
          boardProposedBatchRoute,
          boardImportUploadsRoute,
          boardCalendarConnectedRoute,
          boardHomeRoute,
          boardTeamRoute,
        ]
      : []),
    indexRoute,
    appsRoute,
    retiredDashboardRoute,
    claimSearchRoute,
    claimWizardRoute,
    claimsListRoute,
    claimDetailRoute,
    acceptInviteRoute,
    organizationDeepLinkRoute,
    donationsRoute,
    donationsGrantDetailRoute,
    donationsPaymentsRoute,
    retiredGrantsRoute,
    retiredGrantDetailRoute,
    retiredPaymentRoute,
    // One compiled charity knob reveals the module path, square, nav and sign-in door.
    ...(isFundraisersModuleEnabled()
      ? [
          fundraisersRoute,
          fundraiserCreateRoute,
          fundraiserEditorRoute,
          fundraiserCampaignRoute,
          fundraiserLayersRoute,
          fundraiserSeasonRoute,
          fundraiserDonorsRoute,
          fundraiserMessagesRoute,
          fundraiserEmailsRoute,
          fundraiserEmailRoute,
          ...(isEmailDesignerEnabled() ? [fundraiserEmailDesignRoute] : []),
          ...(isOrgQrDesignEnabled() ? [fundraiserQrRoute] : []),
          fundraiserBoardAskRoute,
          fundraisersTeamRoute,
        ]
      : []),
    // The module and the Donations-owned analytics surface have independent build gates.
    ...(isCrmModuleEnabled()
      ? [contactsRoute, donorDetailRoute, segmentsRoute, newSegmentRoute, segmentRoute]
      : []),
    ...(isCrmImportEnabled() ? [crmImportRoute] : []),
    ...(isCharityDonorAnalyticsEnabled() ? [analyticsRoute, retiredAnalyticsRoute] : []),
    // Compliance nav + routes reveal together (CCM-WEB1; CCM-MOD1B). While the knob is off a
    // direct /compliance URL — or a retired one — falls through to not-found rather than mounting
    // a page whose every backend read 404s behind CHARITY_COMPLIANCE_MODULE_ENABLED. The literal
    // children and the Team alias precede the `$obligationId` route; the eight redirects follow.
    ...(isComplianceUiEnabled()
      ? [
          complianceCalendarRoute,
          complianceFilingsRoute,
          complianceFilingPanelRoute,
          complianceFilingRuleRoute,
          complianceFilingFormRoute,
          complianceFilingSendRoute,
          complianceDocumentsRoute,
          complianceReviewRoute,
          complianceSigningRoute,
          complianceTeamRoute,
          complianceObligationRoute,
          retiredComplianceDeadlinesRoute,
          retiredComplianceFactsRoute,
          retiredFilingRegisterRoute,
          retiredFilingRegisterReviewRoute,
          retiredFilingRegisterDeadlinesRoute,
          retiredFilingRegisterFactsRoute,
          retiredFilingRegisterDocumentsRoute,
          retiredFilingRegisterObligationRoute,
        ]
      : []),
    // The Admin Console (CPA-WEB40): always registered, capability-gated at each route.
    orgRoute,
    orgTeamAccessRoute,
    activityRoute,
    retiredTeamAccessRoute,
    settingsRoute,
    retiredPreferencesRoute,
    moduleTeamRoute,
    ...(isBooksModuleEnabled() ? booksRoutes : []),
    // Back-office setup reveals its routes together (CBO-IN3). While the knob is off a direct
    // /enrollment URL falls through to not-found rather than mounting a page whose every backend read
    // 404s behind CHARITY_BACKOFFICE_ENABLED. Literal before `$param`.
    ...(isEnrollmentUiEnabled()
      ? [enrollmentRoute, enrollmentRecordRoute, enrollmentBackfillRoute]
      : []),
    termsRoute,
    profileRoute,
    ...(isProfilePageEnabled() && isOrgQrDesignEnabled() ? [profileQrRoute] : []),
    removalRoute,
    supportRoute,
    notFoundRoute,
  ]),
]);
